<script lang="ts">
  import { diffRenderer } from '$lib/services/DiffRenderer'
  import type { Segment } from '$lib/stores/aiFeatures'
  import { useTextCounter } from '$lib/composables/useTextCounter'

  /**
   * DiffRendererController: Handles diff rendering and statistics
   * Single Responsibility: Generate and manage diff displays for segments
   */

  interface Props {
    segments: Segment[]
    showDiffOriginal: boolean
    showDiffSuggested: boolean
    originalCharCount?: number
    suggestedCharCount?: number
  }

  let { 
    segments,
    showDiffOriginal = $bindable(true),
    showDiffSuggested = $bindable(true),
    originalCharCount = $bindable(0),
    suggestedCharCount = $bindable(0)
  }: Props = $props()
  

  // Generate diff HTML for each segment - recalculates when segments change
  let segmentDiffs = $derived(
    (segments || []).map(segment => {
      // Always recalculate diff when segment changes
      if (!segment.suggested || segment.suggested === segment.original) {
        return null
      }
      
      
      // Return fresh diff for each segment
      return diffRenderer.renderSideBySide(segment.original, segment.suggested)
    })
  )

  // Get aggregated diff statistics
  let totalStats = $derived((() => {
    let totalAdditions = 0
    let totalDeletions = 0
    
    (segments || []).forEach(segment => {
      if (segment.suggested && segment.suggested !== segment.original) {
        const stats = diffRenderer.getDiffStats(segment.original, segment.suggested)
        totalAdditions += stats.additions
        totalDeletions += stats.deletions
      }
    })
    
    return { additions: totalAdditions, deletions: totalDeletions }
  })())

  // Calculate text for each panel - simplified without IIFE
  let originalText = $derived(
    segments && segments.length > 0
      ? segments
          .map(segment => segment?.original || '')
          .filter(text => text.length > 0)
          .join('\n\n')
      : ''
  )

  // Calculate suggested text - simplified without IIFE
  let suggestedText = $derived(
    segments && segments.length > 0
      ? segments
          .map(segment => segment?.suggested || segment?.original || '')
          .filter(text => text.length > 0)
          .join('\n\n')
      : ''
  )


  // Use useTextCounter for accurate character counting - simplified
  let originalStats = $derived(useTextCounter(originalText))
  let suggestedStats = $derived(useTextCounter(suggestedText))

  // Update bindable character counts when stats change
  $effect(() => {
    originalCharCount = originalStats.characters
    suggestedCharCount = suggestedStats.characters
  })

  // Export as getters for parent component
  export function getSegmentDiffs() {
    return segmentDiffs
  }

  export function getTotalStats() {
    return totalStats
  }

  // Export full stats for more detailed information
  export function getOriginalStats() {
    return originalStats
  }

  export function getSuggestedStats() {
    return suggestedStats
  }

  // Toggle diff display
  export function toggleDiffOriginal() {
    showDiffOriginal = !showDiffOriginal
  }

  export function toggleDiffSuggested() {
    showDiffSuggested = !showDiffSuggested
  }
</script>